import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2 } from 'lucide-react';
import { StructuredIngredient } from '@/types/recipe';

interface IngredientManagerProps {
  ingredients: StructuredIngredient[];
  setIngredients: React.Dispatch<React.SetStateAction<StructuredIngredient[]>>;
}

const IngredientManager: React.FC<IngredientManagerProps> = ({ 
  ingredients, 
  setIngredients 
}) => {
  const addIngredient = () => {
    setIngredients([...ingredients, { name: '', quantity: null, unit: '' }]);
  };

  const removeIngredient = (index: number) => {
    if (ingredients.length > 1) {
      setIngredients(ingredients.filter((_, i) => i !== index));
    }
  };

  const updateIngredient = (index: number, field: keyof StructuredIngredient, value: string | number | null) => {
    const updated = ingredients.map((ingredient, i) => {
      if (i === index) {
        return { ...ingredient, [field]: value };
      }
      return ingredient;
    });
    setIngredients(updated);
  };

  return (
    <div>
      <Label className="text-base font-medium">Ingredients *</Label>
      <div className="space-y-3 mt-2">
        {ingredients.map((ingredient, index) => (
          <div key={index} className="space-y-2">
            {/* Ingredient name - full width on mobile */}
            <Input
              placeholder="Ingredient name"
              value={ingredient.name}
              onChange={(e) => updateIngredient(index, 'name', e.target.value)}
              enterKeyHint="next"
              className="w-full"
            />
            {/* Quantity, Unit, and Delete button - horizontal on second row */}
            <div className="flex gap-2 items-center">
              <Input
                placeholder="Qty"
                type="number"
                step="0.1"
                value={ingredient.quantity || ''}
                onChange={(e) => updateIngredient(index, 'quantity', e.target.value ? parseFloat(e.target.value) : null)}
                inputMode="decimal"
                enterKeyHint="next"
                className="flex-1 min-w-0"
              />
              <Input
                placeholder="Unit"
                value={ingredient.unit || ''}
                onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                enterKeyHint={index === ingredients.length - 1 ? "done" : "next"}
                className="flex-1 min-w-0"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => removeIngredient(index)}
                disabled={ingredients.length === 1}
                className="shrink-0"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          onClick={addIngredient}
          className="w-full"
        >
          <Plus size={16} className="mr-2" />
          Add Ingredient
        </Button>
      </div>
    </div>
  );
};

export default IngredientManager;
